<template>
  <!-- Split auth shell: the brand panel keeps the premium dark treatment while
       the form/content panel follows the active app theme. -->
  <div class="min-h-screen bg-theme-surface text-theme-text" :class="{ 'lg:grid lg:grid-cols-2': $slots.brand }">
    <!-- Brand / marketing panel — always dark (dark token set pinned inline).
         For long content pages (`align="top"`) it sticks to the viewport so the
         brand stays visible while the content column scrolls. -->
    <aside v-if="$slots.brand"
      class="auth-split-brand-panel dark relative hidden min-w-0 items-center justify-center px-10 py-24 lg:flex xl:px-14"
      :class="asideStickyClass"
      :style="brandTokens"
    >
      <router-link to="/" class="absolute left-10 top-8 z-10 flex items-center gap-2 xl:left-14">
        <span
          class="bg-gradient-to-r from-theme-palette-emerald to-theme-palette-emerald bg-clip-text font-azonix text-xl tracking-wide text-transparent"
        >
          PROFITEIA
        </span>
      </router-link>

      <div class="relative z-10 w-full max-w-md">
        <slot name="brand" />
      </div>

      <!-- Profiteia slogan — identical on every auth page. -->
      <p class="absolute inset-x-10 bottom-8 z-10 text-xs text-theme-text-secondary xl:inset-x-14">
        <span class="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-theme-palette-emerald align-middle" />
        {{ t('auth.slogan') }}
      </p>
    </aside>

    <!-- Form panel — theme-adaptive. The top bar hosts the page link and theme
         switch so public split pages can move between light and dark modes. -->
    <main class="auth-split-form-panel relative min-w-0 bg-theme-surface">
      <header class="absolute inset-x-0 top-0 z-10 flex items-center gap-4 p-5 sm:p-6">
        <!-- Compact wordmark only when the brand panel is hidden (mobile) -->
        <router-link to="/" class="flex items-center gap-2" :class="{ 'lg:hidden': $slots.brand }">
          <span
            class="bg-gradient-to-r from-theme-palette-emerald to-theme-palette-emerald bg-clip-text font-azonix text-lg tracking-wide text-transparent"
          >
            PROFITEIA
          </span>
        </router-link>

        <div class="ml-auto flex items-center gap-3">
          <slot name="top-link" />
          <app-icon-button
            variant="soft"
            tone="neutral"
            size="md"
            :label="themeToggleLabel"
            :aria-label="themeToggleLabel"
            @click="toggleThemeMode"
          >
            <icon :path="themeToggleIcon" size="18" />
          </app-icon-button>
        </div>
      </header>

      <div
        class="flex min-h-screen flex-col items-center px-6 py-24 sm:px-10 lg:px-12"
        :class="align === 'top' ? 'justify-start' : 'justify-center'"
      >
        <div class="w-full" :class="containerClass">
          <slot />
        </div>
      </div>

      <!-- Subtle utility links, pinned bottom-right of the form panel. -->
      <footer class="absolute inset-x-0 bottom-0 z-10 p-5 sm:p-6">
        <public-nav-links :exclude="navExclude" :show-login="showLoginLink" />
      </footer>
    </main>
  </div>
</template>

<script setup>
  import Icon from '@/components/Icon';
  import AppIconButton from '@/components/ui/buttons/AppIconButton.vue';
  import PublicNavLinks from '@/components/ui/layout/public/PublicNavLinks.vue';
  import { useDisplayStore } from '@/stores/useDisplayStore';
  import { getThemePreset } from '@/styles/theme/themeManager';
  import { flattenThemeTokens } from '@/styles/theme/themeSchema';
  import { mdiWeatherNight, mdiWeatherSunny } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    /** Width constraint for the form column content. */
    containerClass: {
      type: String,
      default: 'max-w-sm',
    },
    /** `center` (default, auth forms) vertically centres the column; `top`
        anchors it for long content pages and sticks the brand panel. */
    align: {
      type: String,
      default: 'center',
    },
    /** Route names to omit from the bottom-right footer navigation. */
    navExclude: {
      type: Array,
      default: () => [],
    },
    /** Append a "Login" action link to the footer navigation. */
    showLoginLink: {
      type: Boolean,
      default: false,
    },
  });

  const { t } = useI18n();
  const displayStore = useDisplayStore();

  const isDarkMode = computed(() => displayStore.darkMode);
  const themeToggleLabel = computed(() =>
    isDarkMode.value ? 'Switch to light mode' : 'Switch to dark mode'
  );
  const themeToggleIcon = computed(() => (isDarkMode.value ? mdiWeatherSunny : mdiWeatherNight));

  const asideStickyClass = computed(() =>
    props.align === 'top' ? 'lg:sticky lg:top-0 lg:h-screen lg:self-start' : ''
  );

  const brandTokens = computed(() => {
    const preset = getThemePreset('profiteia');
    return flattenThemeTokens(preset.modes.dark || {});
  });

  const toggleThemeMode = () => {
    displayStore.toggleThemeMode();
  };
</script>

<style scoped>
  .auth-split-brand-panel {
    background: linear-gradient(
      135deg,
      rgb(var(--color-auth-brand-start)) 0%,
      rgb(var(--color-auth-brand-mid)) 48%,
      rgb(var(--color-auth-brand-end)) 100%
    );
  }

  .auth-split-form-panel {
    background: rgb(var(--color-auth-form-start));
  }

  .dark .auth-split-form-panel {
    background: linear-gradient(
      135deg,
      rgb(var(--color-auth-form-start)) 0%,
      rgb(var(--color-auth-form-mid)) 58%,
      rgb(var(--color-auth-form-end)) 100%
    );
    box-shadow: inset 18px 0 36px rgb(var(--color-auth-form-shadow) / 0.1);
  }
</style>
